import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Upload, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import OfficeAddressService from './services/OfficeAddressService';

const { TextArea } = Input;

const OfficeAddress = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await OfficeAddressService.getOfficeAddresses();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to load office addresses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      address_line1: record.address_line1,
      address_line2: record.address_line2,
      phone_number: record.phone_number,
      email: record.email,
      office_hours: record.office_hours,
    });
    setSelectedFile(null);
    setPreviewUrl(record.image || null);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Office Address',
      content: 'Are you sure you want to delete this office address?',
      async onOk() {
        try {
          await OfficeAddressService.deleteOfficeAddress(id);
          message.success('Deleted successfully');
          fetch();
        } catch (err) {
          console.error(err);
          message.error('Delete failed');
        }
      }
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error('Invalid image type. Allowed: jpg, png, webp');
      return false;
    }
    if (file.size > maxSize) {
      message.error('Image must be smaller than 5MB');
      return false;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return false;
  };

  const onFinish = async (values) => {
    const payload = {
      address_line1: values.address_line1 || '',
      address_line2: values.address_line2 || '',
      phone_number: values.phone_number || '',
      email: values.email || '',
      office_hours: values.office_hours || '',
    };

    setLoading(true);
    try {
      if (editing) {
        await OfficeAddressService.updateOfficeAddress(editing.id, payload, selectedFile);
        message.success('Updated successfully');
      } else {
        await OfficeAddressService.createOfficeAddress(payload, selectedFile);
        message.success('Created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith && previewUrl.startsWith('blob:')) {
        try { URL.revokeObjectURL(previewUrl); } catch(e){}
      }
      setPreviewUrl(null);
      fetch();
    } catch (err) {
      console.error(err);
      message.error('Save failed');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      title: 'Image', 
      dataIndex: 'image', 
      key: 'image', 
      width: 100,
      render: (v) => v ? <Image src={v} width={60} /> : <span style={{color: '#999'}}>No image</span> 
    },
    { 
      title: 'Address Line 1', 
      dataIndex: 'address_line1', 
      key: 'address_line1',
      render: t => <div style={{maxWidth:250, whiteSpace:'normal'}}>{t || '-'}</div> 
    },
    { 
      title: 'Address Line 2', 
      dataIndex: 'address_line2', 
      key: 'address_line2',
      render: t => <div style={{maxWidth:250, whiteSpace:'normal'}}>{t || '-'}</div> 
    },
    { 
      title: 'Phone', 
      dataIndex: 'phone_number', 
      key: 'phone_number',
      render: t => t || '-'
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email',
      render: t => t || '-'
    },
    { 
      title: 'Office Hours', 
      dataIndex: 'office_hours', 
      key: 'office_hours',
      render: t => <div style={{maxWidth:200, whiteSpace:'normal'}}>{t || '-'}</div> 
    },
    { 
      title: 'Actions', 
      key: 'actions', 
      width: 180,
      render: (_, r) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={()=>handleView(r)}>View</Button>
          <Button icon={<EditOutlined />} size="small" onClick={()=>openEdit(r)}>Edit</Button>
          <Button danger icon={<DeleteOutlined />} size="small" onClick={()=>handleDelete(r.id)}>Delete</Button>
        </Space>
      ) 
    }
  ];

  return (
    <div style={{padding:16}}>
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:12, gap:12}}>
        <h3 style={{margin:0}}>Office Address</h3>
        <div style={{flexShrink:0}}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Office Address</Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{pageSize:10}}
      />

      <Modal 
        title={editing ? 'Edit Office Address' : 'Create Office Address'} 
        open={isModalOpen} 
        onCancel={()=>{
          setIsModalOpen(false); 
          form.resetFields(); 
          setSelectedFile(null); 
          if (previewUrl && previewUrl.startsWith && previewUrl.startsWith('blob:')) { 
            try { URL.revokeObjectURL(previewUrl); } catch(e){} 
          } 
          setPreviewUrl(null);
        }} 
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item 
            name="address_line1" 
            label="Address Line 1" 
            rules={[{required:true, message:'Enter address line 1'}]}
          >
            <Input placeholder="Street address, building name, etc." />
          </Form.Item>

          <Form.Item name="address_line2" label="Address Line 2">
            <Input placeholder="Apartment, suite, unit, floor, etc." />
          </Form.Item>

          <Form.Item 
            name="phone_number" 
            label="Phone Number"
            rules={[{required:true, message:'Enter phone number'}]}
          >
            <Input placeholder="+91 1234567890" />
          </Form.Item>

          <Form.Item 
            name="email" 
            label="Email"
            rules={[
              {required:true, message:'Enter email'},
              {type: 'email', message: 'Enter valid email'}
            ]}
          >
            <Input placeholder="office@example.com" />
          </Form.Item>

          <Form.Item 
            name="office_hours" 
            label="Office Hours"
            rules={[{required:true, message:'Enter office hours'}]}
          >
            <TextArea rows={3} placeholder="Mon-Fri: 9:00 AM - 5:00 PM&#10;Saturday: 9:00 AM - 1:00 PM&#10;Sunday: Closed" />
          </Form.Item>

          <Form.Item label="Office Image">
            <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />}>Choose Image</Button>
            </Upload>
            {previewUrl && <div style={{marginTop:8}}><img src={previewUrl} alt="preview" style={{maxWidth:200}} /></div>}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal 
        title="Office Address Details" 
        open={isViewOpen} 
        onCancel={()=>setIsViewOpen(false)} 
        footer={[<Button key="close" onClick={()=>setIsViewOpen(false)}>Close</Button>]}
        width={700}
      > 
        {viewing && (
          <div>
            {viewing.image && (
              <div style={{marginBottom:16, textAlign:'center'}}>
                <img src={viewing.image} alt="office" style={{maxWidth:'100%', maxHeight:300, borderRadius:8}}/>
              </div>
            )}
            <div style={{background:'#f5f5f5', padding:16, borderRadius:8}}>
              <p><strong>Address Line 1:</strong> {viewing.address_line1 || '-'}</p>
              <p><strong>Address Line 2:</strong> {viewing.address_line2 || '-'}</p>
              <p><strong>Phone Number:</strong> {viewing.phone_number || '-'}</p>
              <p><strong>Email:</strong> {viewing.email || '-'}</p>
              <p><strong>Office Hours:</strong></p>
              <pre style={{background:'white', padding:12, borderRadius:4, whiteSpace:'pre-wrap'}}>
                {viewing.office_hours || '-'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OfficeAddress;
